import * as React from 'react'

export const PRIMARY_CONTENT_ATTRIBUTE = 'data-checkout-primary-content'
export const NEXT_STEP_ATTRIBUTE = 'data-checkout-next-step'
export const SIDEBAR_BOTTOM_OFFSET_VARIABLE = '--checkout-sidebar-bottom-offset'
export const STICKY_TRACK_ATTRIBUTE = 'data-checkout-sticky-track'

/**
 * Padding can only be added to the sidebar while the content stays no taller than the primary content;
 * beyond that it would stretch the whole row and leave empty space instead of aligning.
 * Without a sticky track (legacy basket) there is nothing to cap.
 */
function getFreeSpaceBelowSidebar(container: HTMLElement, lastPrimaryChild: Element) {
  const track = container.querySelector(`[${STICKY_TRACK_ATTRIBUTE}]`)
  const stickyContent = track?.firstElementChild
  if (!track || !stickyContent)
    return Number.POSITIVE_INFINITY

  const stickyContentHeight = stickyContent.getBoundingClientRect().height + (Number.parseFloat(getComputedStyle(stickyContent).marginBottom) || 0)
  const availableHeight = lastPrimaryChild.getBoundingClientRect().bottom - track.getBoundingClientRect().top
  return Math.max(0, availableHeight - stickyContentHeight)
}

/**
 * Publishes, as a CSS variable on the returned element, the distance between the bottom of the last component
 * before the next-step button and the bottom of the primary content, so the sidebar can end aligned with that component.
 * The primary content can render after the sidebar, so it is re-measured whenever the content tree changes or resizes.
 */
export function useSidebarBottomOffset() {
  const ref = React.useRef<HTMLDivElement>(null)

  React.useLayoutEffect(() => {
    const container = ref.current
    if (!container)
      return

    let resizeObserver: ResizeObserver | undefined

    const update = () => {
      const nextStep = container.querySelector(`[${NEXT_STEP_ATTRIBUTE}]`)
      const primaryContent = nextStep?.closest(`[${PRIMARY_CONTENT_ATTRIBUTE}]`)
      const lastComponent = nextStep?.previousElementSibling
      // The primary content stretches to the sidebar height, so its own bottom would feed the offset back into itself
      const lastPrimaryChild = primaryContent?.lastElementChild

      if (!lastPrimaryChild || !lastComponent) {
        container.style.removeProperty(SIDEBAR_BOTTOM_OFFSET_VARIABLE)
        return
      }

      resizeObserver?.observe(lastPrimaryChild)
      resizeObserver?.observe(lastComponent)
      const stickyContent = container.querySelector(`[${STICKY_TRACK_ATTRIBUTE}]`)?.firstElementChild
      if (stickyContent)
        resizeObserver?.observe(stickyContent)
      const alignedOffset = Math.max(0, lastPrimaryChild.getBoundingClientRect().bottom - lastComponent.getBoundingClientRect().bottom)
      const offset = Math.min(alignedOffset, getFreeSpaceBelowSidebar(container, lastPrimaryChild))
      container.style.setProperty(SIDEBAR_BOTTOM_OFFSET_VARIABLE, `${offset}px`)
    }

    const mutationObserver = new MutationObserver(update)
    mutationObserver.observe(container, { childList: true, subtree: true })
    resizeObserver = new ResizeObserver(update)
    resizeObserver.observe(container)
    update()

    return () => {
      mutationObserver.disconnect()
      resizeObserver?.disconnect()
    }
  }, [])

  return ref
}
